import type { ComponentPropsWithoutRef } from 'react';
import { Link } from 'react-router-dom';
import ReactMarkdown, { defaultUrlTransform, type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { cn } from '../../lib/cn';

const LINK_CLASS = 'text-link font-medium hover:text-action-hover hover:underline focus-ring rounded';

/**
 * Anchor renderer for (untrusted, LLM-generated) markdown: in-app paths → react-router <Link>;
 * http(s) → new tab; mailto/tel/xmpp → plain anchor; anything else → inert text.
 *
 * SECURITY: only a SINGLE leading slash NOT followed by `/` or `\` is an in-app path. A
 * protocol-relative `//host` (or `/\host`, which browsers normalize to `//host`) has no colon so
 * react-markdown's sanitizer passes it through — treating it as internal would render
 * `<Link to="//evil.com">`, an off-site open-redirect. Those fall through to inert text instead.
 */
function MarkdownLink({ href, children }: ComponentPropsWithoutRef<'a'>) {
  if (href && /^\/(?![/\\])/.test(href)) {
    return (
      <Link to={href} className={LINK_CLASS}>
        {children}
      </Link>
    );
  }
  if (href && /^https?:\/\//i.test(href)) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={LINK_CLASS}>
        {children}
      </a>
    );
  }
  // mailto:/tel:/xmpp: — a normal anchor (no new tab); react-markdown already stripped unsafe
  // protocols (javascript:, data:, …) to empty, so those + any other odd href render as text.
  if (href && /^(mailto:|tel:|xmpp:)/i.test(href)) {
    return (
      <a href={href} className={LINK_CLASS}>
        {children}
      </a>
    );
  }
  return <>{children}</>;
}

/**
 * Chat-tuned element styles (no @tailwindcss/typography). Tight vertical rhythm so rendered
 * markdown sits naturally inside a message bubble; `last:mb-0` avoids trailing space.
 */
const COMPONENTS: Components = {
  a: MarkdownLink,
  p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
  h1: ({ children }) => <h1 className="mb-2 mt-1 text-base font-bold text-ink">{children}</h1>,
  h2: ({ children }) => <h2 className="mb-2 mt-1 text-base font-bold text-ink">{children}</h2>,
  h3: ({ children }) => (
    <h3 className="mb-1.5 mt-1 text-sm font-semibold text-ink">{children}</h3>
  ),
  h4: ({ children }) => (
    <h4 className="mb-1.5 mt-1 text-sm font-semibold text-ink">{children}</h4>
  ),
  ul: ({ children }) => <ul className="mb-2 ml-5 list-disc space-y-1 last:mb-0">{children}</ul>,
  ol: ({ children }) => <ol className="mb-2 ml-5 list-decimal space-y-1 last:mb-0">{children}</ol>,
  li: ({ children }) => <li className="marker:text-ink-faint">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-ink">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  blockquote: ({ children }) => (
    <blockquote className="mb-2 border-l-2 border-line pl-3 text-ink-label last:mb-0">
      {children}
    </blockquote>
  ),
  hr: () => <hr className="my-3 border-line" />,
  code: ({ className, children }) => {
    // v9 has no `inline` prop: a fenced block carries a `language-*` class OR spans multiple lines;
    // block code sits inside <pre> (a fixed dark bg in both themes), so it stays transparent and
    // takes the <pre>'s fixed text colour — inline code gets a themed pill.
    const isBlock = (className?.startsWith('language-') ?? false) || String(children).includes('\n');
    return isBlock ? (
      <code className={cn('font-mono', className)}>{children}</code>
    ) : (
      <code className="rounded bg-chip px-1 py-0.5 font-mono text-[0.85em] text-ink">
        {children}
      </code>
    );
  },
  pre: ({ children }) => (
    <pre className="mb-2 overflow-x-auto rounded-lg bg-chrome-control/90 p-3 text-xs leading-relaxed text-chrome-text last:mb-0">
      {children}
    </pre>
  ),
  table: ({ children }) => (
    <div className="mb-2 overflow-x-auto last:mb-0">
      <table className="w-full border-collapse text-left text-xs">{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th className="border border-line px-2 py-1 font-semibold">{children}</th>
  ),
  td: ({ children }) => <td className="border border-line-soft px-2 py-1 align-top">{children}</td>,
};

/**
 * Render a markdown string as safe React elements (no dangerouslySetInnerHTML). GFM tables/strike/
 * task-lists + soft line breaks (`remark-breaks`, so a single newline shows as a line break — LLM
 * chat output relies on that). Used for the Content Strategist's assistant messages.
 */
export function Markdown({ content, className }: { content: string; className?: string }) {
  return (
    <div className={cn('text-sm leading-relaxed', className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        // Default transform drops `tel:` (not in its allowlist), which would make the tel branch in
        // MarkdownLink dead. Allow tel: through; everything else keeps the default sanitization
        // (javascript:/data:/etc. still stripped to '').
        urlTransform={(url) => (/^tel:/i.test(url) ? url : defaultUrlTransform(url))}
        components={COMPONENTS}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
